import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Building2,
  CalendarCheck,
  ChevronRight,
  CreditCard,
  IndianRupee,
  LayoutList,
  ListPlus,
  MessageCircle,
  Plug,
  Route,
  Ruler,
  ShieldCheck,
  Store,
  Ticket,
  Truck,
  Users,
  Workflow,
} from 'lucide-react';
import type { PlanModule } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge } from '@tailoring-crm/ui';
import { useHasModule } from '../lib/billing/entitlements.js';

/**
 * The settings hub.
 *
 * A list of the shop's setup screens, growing one entry at a time as the tasks that own them
 * land. Measurements (task 8) was the first, WhatsApp (task 14) the second; the empty state
 * below still speaks for the rest, so the screen never looks finished when it is not.
 */
interface SettingsSection {
  to: string;
  icon: typeof Users;
  titleKey: string;
  descriptionKey: string;
  /** The plan module behind this screen. Absent means `core`, which every plan includes. */
  module?: PlanModule;
}

const SECTIONS: readonly SettingsSection[] = [
  {
    // First, because it is the first thing a new shop is asked for: who is on the bill.
    to: '/settings/shop',
    icon: Store,
    titleKey: 'settings.shopDetails.title',
    descriptionKey: 'settings.shopDetails.description',
  },
  {
    // The roster is not a setting, but the bottom bar has five slots and no sixth — and this
    // hub is where an owner already comes to set the shop up, which includes hiring for it.
    to: '/staff',
    icon: Users,
    titleKey: 'staff.title',
    descriptionKey: 'staff.description',
  },
  {
    // The commercial relationship. The API refuses everyone but the owner, and the screen
    // says so rather than 404-ing a manager who taps it.
    to: '/settings/billing',
    icon: CreditCard,
    titleKey: 'billing.title',
    descriptionKey: 'billing.description',
  },
  {
    to: '/settings/measurements',
    icon: Ruler,
    titleKey: 'settings.measurementsTitle',
    descriptionKey: 'settings.measurementsDescription',
  },
  {
    // Every module's own fields on one board, a column each.
    to: '/settings/custom-fields',
    icon: ListPlus,
    titleKey: 'customFields.title',
    descriptionKey: 'customFields.description',
  },
  {
    // When the shop expects people in, and how much lateness is lateness. It is here rather than
    // on the attendance screen because it is the shape of that screen's judgement, edited twice
    // and read every day.
    to: '/settings/attendance',
    icon: CalendarCheck,
    titleKey: 'attendance.settings.title',
    descriptionKey: 'attendance.settings.description',
  },
  {
    // The shop's stages themselves, before the per-garment routes that narrow them.
    to: '/settings/workflow',
    icon: Workflow,
    titleKey: 'settings.workflow.title',
    descriptionKey: 'settings.workflow.description',
  },
  {
    // Which stages each garment walks. Beside the fields and pipeline editors because it is the
    // same kind of decision — the shape of a path something travels — and because the person who
    // finds the blouse going through ironing is the person who came here to stop it.
    to: '/settings/stage-flows',
    icon: Route,
    titleKey: 'settings.stageFlows.title',
    descriptionKey: 'settings.stageFlows.description',
  },
  {
    // The speeds the counter picks from. Beside the rate card because the two are the same kind
    // of thing: numbers typed once that the booking screen then fills in by itself.
    to: '/settings/delivery-schedules',
    icon: Truck,
    titleKey: 'settings.deliverySchedules.title',
    descriptionKey: 'settings.deliverySchedules.description',
  },
  {
    // The shop's own codes. Beside the rate card because both are money decided once here and
    // then applied by the counter — the difference is that a coupon can be counted afterwards.
    to: '/settings/coupons',
    icon: Ticket,
    titleKey: 'coupons.title',
    descriptionKey: 'coupons.description',
  },
  {
    // Where to pay, how orders are numbered, and the mark on the shop's photographs. One page
    // because all three are the same question — how the shop presents itself — and none of
    // them is big enough to be worth its own entry.
    to: '/settings/presentation',
    icon: Building2,
    titleKey: 'presentation.title',
    descriptionKey: 'presentation.description',
  },
  {
    to: '/settings/form-layouts',
    icon: LayoutList,
    titleKey: 'formLayouts.title',
    descriptionKey: 'formLayouts.description',
  },
  {
    to: '/settings/rates',
    icon: IndianRupee,
    titleKey: 'staff.rates.defaultsTitle',
    descriptionKey: 'settings.ratesDescription',
  },
  {
    // Where the shop is wired to the outside world. Above the messages row because connecting
    // comes before deciding what to say, and because this is now the only place a workspace
    // is connected.
    //
    // No module: the SCREEN is core and every plan may reach it — the lock lives on the card
    // inside, which is what a plan actually decides. A "not in your plan" badge here would
    // claim the whole screen is out of reach, and it is not.
    to: '/settings/integrations',
    icon: Plug,
    titleKey: 'settings.integrationsTitle',
    descriptionKey: 'settings.integrationsDescription',
  },
  {
    to: '/settings/notifications',
    icon: MessageCircle,
    titleKey: 'settings.notificationsTitle',
    descriptionKey: 'settings.notificationsDescription',
    // WhatsApp is a Growth feature. The entry is MARKED rather than hidden — a shop that has
    // downgraded still has its message history behind this link, and removing the link would
    // read as having lost it. See the note on the badge below.
    module: 'relay_whatsapp',
  },
  {
    to: '/settings/security',
    icon: ShieldCheck,
    titleKey: 'settings.securityTitle',
    descriptionKey: 'settings.securityDescription',
  },
] as const;

export function SettingsPage(): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            {t('settings.sectionsHeading')}
          </h3>
          <ul className="flex flex-col gap-2">
            {SECTIONS.map((section) => (
              <li key={section.to}>
                <SettingsLink section={section} />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

/**
 * One row of the hub, marked when the shop's plan does not include it.
 *
 * MARKED, not hidden, and the distinction is the whole reason this is a component rather than
 * a filter on the list above. A shop that downgraded still has its message history behind this
 * link; removing the link would read as having lost it, which is precisely the fear that stops
 * people downgrading instead of cancelling. The badge is honest — "not in your plan" — and the
 * screen behind it is read-only, exactly as the API enforces.
 *
 * The entitlement comes from the shared service the API refuses with, so this badge and that
 * refusal cannot disagree.
 */
function SettingsLink({ section }: { section: SettingsSection }): ReactElement {
  const { t } = useTranslation();
  const { icon: Icon } = section;
  const entitled = useHasModule(section.module ?? 'core');

  return (
    <Link
      to={section.to}
      className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 font-medium">
          {t(section.titleKey)}
          {entitled ? null : <Badge variant="secondary">{t('settings.notInPlan')}</Badge>}
        </span>
        <span className="block text-sm text-muted-foreground">{t(section.descriptionKey)}</span>
      </span>
      <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

export default SettingsPage;
